/**
 * Citation Badge Component
 *
 * Renders an interactive inline citation badge [📄 N] with a hover/click popover
 * showing the full citation content, document name, and relevance score.
 *
 * Behavior:
 * - Hover: Shows popover temporarily
 * - Click: Pins popover open
 * - Click outside: Dismisses pinned popover
 *
 * TODO: Add unit tests for portal positioning logic (above/below viewport bounds)
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { SourceDocumentDialog } from './SourceDocumentDialog';
export function CitationBadge({ citation }) {
    const [showSource, setShowSource] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState(null);
    const buttonRef = useRef(null);
    const popoverRef = useRef(null);
    const isOpen = isPinned || isHovering;
    // Calculate popover position when opening
    useLayoutEffect(()=>{
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            // Hardcoded dimensions for simplicity - these match the Tailwind classes below
            // and provide consistent positioning without needing to measure the DOM
            const popoverWidth = 320; // w-80 = 20rem = 320px
            const popoverHeight = 200; // Approximate max height based on typical citation content
            // Position above the button, centered horizontally
            let left = rect.left + rect.width / 2 - popoverWidth / 2;
            let top = rect.top - popoverHeight - 20;
            // Keep within viewport bounds
            if (left < 8) left = 8;
            if (left + popoverWidth > window.innerWidth - 8) {
                left = window.innerWidth - popoverWidth - 8;
            }
            if (top < 8) {
                // If not enough space above, show below
                top = rect.bottom + 8;
            }
            setPopoverPosition({
                top,
                left
            });
        }
    }, [
        isOpen
    ]);
    // Use custom hook for click outside detection on the popover
    useClickOutside(popoverRef, ()=>{
        if (isPinned) {
            setIsPinned(false);
        }
    });
    const popoverContent = isOpen && popoverPosition && /*#__PURE__*/ _jsx("div", {
        ref: popoverRef,
        style: {
            position: 'fixed',
            top: popoverPosition.top,
            left: popoverPosition.left,
            zIndex: 9999
        },
        className: `w-80 p-4 bg-white dark:bg-gray-900
                 border border-gray-200 dark:border-gray-700
                 shadow-xl rounded-lg
                 ${isPinned ? 'pointer-events-auto' : 'pointer-events-none'}`,
        role: isPinned ? 'dialog' : 'tooltip',
        onMouseEnter: ()=>setIsHovering(true),
        onMouseLeave: ()=>setIsHovering(false),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-lg",
                                    children: "📄"
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    id: `citation-${citation.id}-title`,
                                    className: "text-sm font-semibold text-gray-900 dark:text-gray-100",
                                    children: [
                                        "Source ",
                                        citation.id
                                    ]
                                })
                            ]
                        }),
                        citation.relevance && /*#__PURE__*/ _jsxs("span", {
                            className: "text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
                            children: [
                                Math.round(citation.relevance * 100),
                                "% relevant"
                            ]
                        })
                    ]
                }),
                citation.documentName && /*#__PURE__*/ _jsx("div", {
                    className: "text-xs text-gray-600 dark:text-gray-400 font-medium",
                    children: citation.documentName
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "border-t border-gray-200 dark:border-gray-700"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm text-gray-700 dark:text-gray-300 leading-relaxed",
                    children: [
                        '"',
                        citation.content,
                        '"'
                    ]
                }),
                /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: "text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center gap-1 pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed",
                    disabled: !citation.documentId,
                    onClick: ()=>{
                        setIsPinned(false);
                        setIsHovering(false);
                        setShowSource(true);
                    },
                    children: "View Full Context →"
                })
            ]
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("button", {
                ref: buttonRef,
                type: "button",
                className: "inline-flex items-center gap-1 align-baseline text-xs font-medium px-1.5 py-0.5 rounded-md mx-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200 border border-blue-300 dark:border-blue-700/50 cursor-pointer",
                "aria-label": `Citation ${citation.id}`,
                "aria-expanded": isOpen || showSource,
                "aria-haspopup": "dialog",
                onMouseEnter: ()=>setIsHovering(true),
                onMouseLeave: ()=>setIsHovering(false),
                onClick: (e)=>{
                    e.stopPropagation();
                    if (citation.documentId) {
                        setIsHovering(false);
                        setIsPinned(false);
                        setShowSource(true);
                    } else {
                        setIsPinned(!isPinned);
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[10px]",
                        children: "📄"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: citation.id
                    })
                ]
            }),
            popoverContent && /*#__PURE__*/ createPortal(popoverContent, document.body),
            showSource && citation.documentId ? /*#__PURE__*/ _jsx(SourceDocumentDialog, {
                documentId: citation.documentId,
                quote: citation.content,
                sourceAnchor: citation.sourceAnchor,
                name: citation.documentName ?? 'Source document',
                onClose: ()=>setShowSource(false)
            }) : null
        ]
    });
}
